import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Button, Text } from "@mantine/core"
import { IconFile, IconTrash } from "@tabler/icons-react"
import { useCallback, useMemo, useRef } from "react"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import {
  deleteMeetingMaterial,
  digitizeMeetingMaterial,
  fetchMeetingMaterials,
  type IMeetingMaterial,
  reanalyzeMeetingMaterials,
  uploadMeetingMaterial,
} from "../../lib/api"
import { extractErrorMessage } from "../../lib/api/errors"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

function formatClock(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, "0")}`
}

function isImage(mime: string): boolean {
  return mime.startsWith("image/")
}

export const MeetingMaterials = ({ meetingId, companyId, processed = false }: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const inputRef = useRef<HTMLInputElement>(null)

  const listQuery = useCompanyQuery({
    key: (org) => queryKeys.meetingMaterials(org, meetingId),
    queryFn: (client) => fetchMeetingMaterials(client, meetingId),
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? []
      return items.some((m) => m.status === "extracting" || m.status === "stored") ? 2000 : false
    },
  })

  const items = listQuery.data?.items ?? []
  const unusedCount = listQuery.data?.unusedCount ?? 0

  const uploadMutation = useCompanyMutation({
    mutationFn: async (_client, file: File) =>
      uploadMeetingMaterial(meetingId, file, { kind: "archive", origin: "upload" }),
    onSuccess: () => listQuery.refetch(),
    onError: (err) => notifyError(extractErrorMessage(err)),
  })

  const deleteMutation = useCompanyMutation({
    mutationFn: (client, materialId: string) => deleteMeetingMaterial(client, meetingId, materialId),
    onSuccess: () => listQuery.refetch(),
    onError: (err) => notifyError(extractErrorMessage(err)),
  })

  const digitizeMutation = useCompanyMutation({
    mutationFn: (client, materialId: string) => digitizeMeetingMaterial(client, meetingId, materialId),
    onSuccess: () => listQuery.refetch(),
    onError: (err) => notifyError(extractErrorMessage(err)),
  })

  const reanalyzeMutation = useCompanyMutation({
    mutationFn: (client) => reanalyzeMeetingMaterials(client, meetingId),
    onSuccess: () => listQuery.refetch(),
    onError: (err) => notifyError(extractErrorMessage(err)),
  })

  const handleFiles = useCallback(
    (list: FileList | null): void => {
      if (!list) return
      for (const file of Array.from(list)) {
        uploadMutation.mutate(file)
      }
    },
    [uploadMutation],
  )

  const showReanalyze = processed && unusedCount > 0

  const fileUrl = useCallback(
    (item: IMeetingMaterial): string => {
      if (item.fileUrl.startsWith("http") || item.fileUrl.startsWith("/")) return item.fileUrl
      return `/api/companies/${companyId}/meetings/${meetingId}/materials/${item.id}/file`
    },
    [companyId, meetingId],
  )

  const empty = items.length === 0

  const dropLabel = useMemo(() => i18n._(msg`Drop a photo, PDF, Word, or Excel file — or click to add`), [i18n])

  return (
    <section className={classes.section} data-testid="meeting-materials">
      <div className={classes.head}>
        <h2 className={classes.title}>
          <Trans>Materials</Trans>
        </h2>
      </div>

      {showReanalyze && (
        <div className={classes.banner}>
          <p className={classes.bannerText}>
            <Trans>
              These files were not used in the notes. Reanalyze to fold them in. This does not re-transcribe.
            </Trans>
          </p>
          <Button size="xs" loading={reanalyzeMutation.isPending} onClick={() => reanalyzeMutation.mutate()}>
            <Trans>Reanalyze notes</Trans>
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <div className={classes.strip}>
          {items.map((item) => (
            <article key={item.id} className={classes.card}>
              <a className={classes.thumb} href={fileUrl(item)} target="_blank" rel="noreferrer">
                {isImage(item.mime) ? (
                  <img src={fileUrl(item)} alt={item.filename} />
                ) : (
                  <div className={classes.docFace}>
                    <IconFile size={22} />
                    <span>{item.filename}</span>
                  </div>
                )}
                {item.capturedAtSeconds !== null && (
                  <span className={classes.stamp}>{formatClock(item.capturedAtSeconds)}</span>
                )}
              </a>
              <span className={classes.name} title={item.filename}>
                {item.filename}
              </span>
              {item.interpretation && (
                <Text className={classes.derived} size="xs">
                  <Trans>Diagram from this photo</Trans>
                </Text>
              )}
              <div>
                {isImage(item.mime) && !item.interpretation && item.status === "ready" && (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    loading={digitizeMutation.isPending}
                    onClick={() => digitizeMutation.mutate(item.id)}
                  >
                    <Trans>Turn into a diagram</Trans>
                  </Button>
                )}
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="danger"
                  aria-label={i18n._(msg`Remove ${item.filename}`)}
                  onClick={() => deleteMutation.mutate(item.id)}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </div>
            </article>
          ))}
        </div>
      )}

      <button
        type="button"
        className={classes.drop}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
        }}
        onDrop={(e) => {
          e.preventDefault()
          handleFiles(e.dataTransfer.files)
        }}
      >
        {empty ? dropLabel : i18n._(msg`Add another file`)}
      </button>
      <input
        ref={inputRef}
        className={classes.hiddenInput}
        type="file"
        accept="image/*,.pdf,.docx,.xlsx"
        multiple
        onChange={(e) => {
          handleFiles(e.currentTarget.files)
          e.currentTarget.value = ""
        }}
      />
    </section>
  )
}
